import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';

import { ConstantsService } from 'app/services/constants-service';
import { CurrencyService } from 'app/shared/services/currency-service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { ValidateAmount, FormErrors } from 'app/shared/validators/generic-validators';

import { BankAccount } from 'app/shared/models/bank-account.model';
import { PaymentModel } from 'app/shared/models/payment.model';

import { FinanceService } from 'app/finance/finance.service';
import { PaymentService } from './../payment.service';
import { MonitoringStatusService } from 'app/shared/services/monitoring-status.service';

@Component({
    selector: 'payment',
    templateUrl: './payment.component.html'
})

/**
 * Component to add/save a payment instruction.
 */
export class PaymentComponent implements OnInit {
    paymentForm: FormGroup;
    savingPayment: boolean;
    selectedPayment: PaymentModel;

    paymentId: number;
    clientId: number;
    clientEntityId: number;
    counterPartyEntityId: number;
    clientAccounts: BankAccount[];
    counterPartyAccounts: BankAccount[];
    currencies: string[];
    action: string;

    paymentClientSearchURI = ConstantsService.CLIENT_SEARCH;
    clientPlaceHolder = MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER;
    counterPartyPlaceHolder = MessageService.COUNTER_PARTY_SEARCH_PLACEHOLDER;

    formErrors = {
        'clientName': '',
        'clientAccountNo': '',
        'counterPartyName': '',
        'counterPartyAccountNo': '',
        'amount': '',
        'currency': '',
        'details': '',
        'reference': '',
        'transactionDate': ''
    };

    validationMessages = {
        'clientName': {
            'required': 'Required field value.',
            'invalid': 'Invalid client name.',
        },
        'clientAccountNo': {
            'required': 'Required field value.',
        },
        'counterPartyName': {
            'required': 'Required field value.',
            'invalid': 'Invalid counter party name.',
        },
        'counterPartyAccountNo': {
            'required': 'Required field value.',
            'account': 'Invalid account selected: duplicate.',
        },
        'amount': {
            'required': 'Required field value.',
            'numeric': 'Only positive numbers are allowed.',
            'decimal': 'Use \'dot\' as decimal symbol.',
        },
        'currency': {
            'required': 'Required field value.',
        },
        'details': {
            'required': 'Required field value.',
        },
        'reference': {
            'required': 'Required field value.',
        },
        'transactionDate': {
            'required': 'Required field value.',
        }
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private currencyService: CurrencyService,
        private financeService: FinanceService,
        private paymentService: PaymentService,
        private snackBarService: SnackBarService,
        private trackingService: TrackingService,
        private monitoringStatus: MonitoringStatusService) { }

    createForm() {
        this.paymentForm = this.formBuilder.group({
            clientName: ['', Validators.required],
            clientAccountNo: ['', Validators.required],
            counterPartyName: ['', Validators.required],
            counterPartyAccountNo: ['', [Validators.required, this.validateAccounts]],
            amount: ['', [Validators.required, ValidateAmount]],
            currency: ['', Validators.required],
            details: ['', Validators.required],
            reference: ['', Validators.required],
            transactionDate: ['', Validators.required],
            chargeType: [''],
            remarks: ['']
        });
        this.paymentForm.valueChanges.subscribe(data => this.onValueChanged(data));
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    ngOnInit() {
        this.createForm();
        this.setCurrencies();
        this.updatePaymentForm();
        this.onValueChanged();
    }

    onValueChanged(data?: any) {
        if (!this.paymentForm) {
            return;
        }
        FormErrors('paymentForm', this);
    }

    onClientNameChanged(value) {
        this.checkNameInput(value, 'clientName');
    }

    onCounterPartyNameChanged(value) {
        this.checkNameInput(value, 'counterPartyName');
    }

    onClientSelectionEvent(searchDetails: GenericSearchDetails) {
        if (!this.validateSearchSelection(searchDetails)) {
            return;
        }
        this.clientSelected(searchDetails, 'clientName');
    }

    validateSearchSelection(searchDetails) {
        const monitoringStatus = this.monitoringStatus.checkSearchMonitoringStatus(searchDetails);
        if (monitoringStatus && !confirm(monitoringStatus)) {
            return false;
        }
        return true;
    }

    onCounterPartySelectionEvent(searchDetails: GenericSearchDetails) {
        if (!this.validateSearchSelection(searchDetails)) {
            return;
        }
        this.clientSelected(searchDetails, 'counterPartyName');
    }

    onSubmit({ value, valid }) {
        if (confirm(MessageService.SAVE_PAYMENT_ALERT)) {
            const msgSuccess = this.action === 'Add' ? MessageService.ADD_PAYMENT_SUCCESS : MessageService.EDIT_PAYMENT_SUCCESS;
            const msgFailure = this.action === 'Add' ? MessageService.ADD_PAYMENT_FAILURE : MessageService.EDIT_PAYMENT_FAILURE;

            const paymentModel = new PaymentModel();
            paymentModel.id = this.paymentId;
            paymentModel.clientId = this.clientId;
            paymentModel.clientName = value.clientName;
            paymentModel.clientTxnAccountNo = value.clientAccountNo;
            paymentModel.counterPartyEntityId = this.counterPartyEntityId;
            paymentModel.counterPartyName = value.counterPartyName;
            paymentModel.counterPartyTxnAccNo = value.counterPartyAccountNo;
            paymentModel.amount = value.amount;
            paymentModel.chargeType = value.chargeType;
            paymentModel.currency = value.currency;
            paymentModel.communication = value.details;
            paymentModel.reference = value.reference;
            paymentModel.remarks = value.remarks;
            paymentModel.txnDate = value.transactionDate;

            this.savingPayment = true;
            this.paymentService.savePayment(paymentModel)
                .subscribe(response => {
                    this.savingPayment = false;
                    if (response.flagResult) {
                        this.dialogRef.close(true);
                    }
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.PAYMENTS,
                        TrackingConstants.SAVE_PAYMENT,
                        TrackingConstants.SUCCESS, 1);
                    this.callSnackBarService(response, msgSuccess, msgFailure);
                },
                err => {
                    this.savingPayment = false;
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.PAYMENTS,
                        TrackingConstants.SAVE_PAYMENT,
                        TrackingConstants.FAILURE, 0);
                    this.callSnackBarService(err, msgSuccess, msgFailure);
                }
                );
        }
    }

    updatePaymentForm(): void {
        if (this.selectedPayment) {
            for (const inner in this.paymentForm.controls) {
                this.paymentForm.get(inner).markAsTouched();
                this.paymentForm.get(inner).updateValueAndValidity();
            }
            const date = new Date(this.selectedPayment.txnDate);
            const formattedDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
            this.paymentId = this.selectedPayment.id;
            this.clientId = this.selectedPayment.clientId;
            this.clientEntityId = this.selectedPayment.clientEntityId;
            this.clientAccounts = this.selectedPayment.clientAccounts;
            this.counterPartyEntityId = this.selectedPayment.counterPartyEntityId;
            this.counterPartyAccounts = this.selectedPayment.counterPartyAccounts;

            this.paymentForm.patchValue({
                clientName: this.selectedPayment.clientName,
                clientAccountNo: this.selectedPayment.clientTxnAccountNo,
                counterPartyName: this.selectedPayment.counterPartyName,
                counterPartyAccountNo: this.selectedPayment.counterPartyTxnAccNo,
                amount: this.selectedPayment.amount,
                chargeType: this.selectedPayment.chargeType,
                currency: this.selectedPayment.currency,
                details: this.selectedPayment.communication,
                remarks: this.selectedPayment.remarks,
                reference: this.selectedPayment.reference,
                transactionDate: formattedDate
            });

            // @todo extend this check >> specified accounts need to be included in arrays
            if (this.selectedPayment.clientTxnAccountNo && this.clientAccounts.length === 0
                || this.selectedPayment.counterPartyTxnAccNo && this.counterPartyAccounts.length === 0) {
            }
        }
    }

    private checkNameInput(value, ct) {
        let accounts;
        const nameControl = this.paymentForm.controls[ct];
        if (ct === 'clientName') {
            accounts = 'clientAccounts';
            this.paymentForm.controls['clientAccountNo'].setValue(null);
        } else {
            accounts = 'counterPartyAccounts';
            this.paymentForm.controls['counterPartyAccountNo'].setValue(null);
        }
        this[accounts] = [];
        if (!!value && typeof value === 'string' && value !== nameControl.value && nameControl.touched) {
            this[(ct === 'clientName' ? 'clientId' : 'counterPartyId')] = null;
            nameControl.setErrors({ 'invalid': this.validationMessages[ct]['invalid'] });
            nameControl.markAsTouched();
        } else if (!value) {
            nameControl.markAsUntouched();
        }
    }

    private clientSelected(searchDetails, clientType) {
        const nameControl = this.paymentForm.controls[clientType];
        nameControl.setValue(searchDetails.clientName);
        nameControl.markAsTouched();
        if (!!nameControl.errors && nameControl.errors.hasOwnProperty('invalid')) {
            delete nameControl.errors['invalid'];
        }
        if (clientType === 'clientName') {
            this.clientId = searchDetails.clientId;
            this.updateAccounts(searchDetails.entityId, 'clientAccounts');
        } else {
            this.counterPartyEntityId = searchDetails.entityId;
            this.updateAccounts(searchDetails.entityId, 'counterPartyAccounts');
        }
    }

    private setCurrencies() {
        this.currencyService.getCurrencies()
            .subscribe(currencies => {
                this.currencies = currencies.arrResult;
            });
    }

    private updateAccounts(entityId: number, clientIdentifier: string) {
        this.financeService.getBankAccountsForEntity(entityId)
            .subscribe(response => {
                if (response.message) {
                    this.callSnackBarService(response, '', '');
                } else {
                    this[clientIdentifier] = response;
                }
            });
    }

    validateAccounts(control: FormControl) {
        const primaryAccountNo = control.value;
        if (!!primaryAccountNo) {
            if (primaryAccountNo === control.parent.controls['clientAccountNo'].value) {
                return { account: true };
            }
        }
        return null;
    }
}



// WEBPACK FOOTER //
// ./src/app/finance/payments/payment/payment.component.ts